import { Info } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Modal } from '@/components/ui/Modal'
import { useAuth } from '@/contexts/AuthContext'
import { CHANGELOG_VISIBLE_RELEASES, parseChangelog } from '@repo/types'
import changelogRaw from '../../../../../CHANGELOG.md?raw'

const VERSION = import.meta.env.VITE_APP_VERSION || 'dev'
const BUILD_TIME = import.meta.env.VITE_BUILD_TIME || ''
const GIT_SHA = import.meta.env.VITE_GIT_SHA || ''
const RELEASES = parseChangelog(changelogRaw).slice(0, CHANGELOG_VISIBLE_RELEASES)

// Per-user key (the "what's new" indicator is read/unread per user, not per
// browser — otherwise one user on a shared machine clears it for everyone).
const lastSeenKeyFor = (userId?: string) => `lastSeenVersion:${userId ?? 'anon'}`

function formatBuildTime(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return new Intl.DateTimeFormat('sk-SK', { dateStyle: 'medium', timeStyle: 'short' }).format(date)
}

export function VersionBadge() {
  const { user } = useAuth()
  const lastSeenKey = lastSeenKeyFor(user?.id)
  const [hoverOpen, setHoverOpen] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [lastSeen, setLastSeen] = useState(() => localStorage.getItem(lastSeenKey))
  const closeTimer = useRef<ReturnType<typeof setTimeout>>()

  // Re-read when the active user changes (e.g. re-login in the same browser).
  useEffect(() => {
    setLastSeen(localStorage.getItem(lastSeenKey))
  }, [lastSeenKey])

  // Clear any pending close timer on unmount.
  useEffect(() => () => clearTimeout(closeTimer.current), [])

  // Small grace period so moving the cursor across the gap from the icon to the
  // bubble (and its "Čo je nové" link) doesn't immediately dismiss it.
  function openBubble() {
    clearTimeout(closeTimer.current)
    setHoverOpen(true)
  }
  function scheduleCloseBubble() {
    clearTimeout(closeTimer.current)
    closeTimer.current = setTimeout(() => setHoverOpen(false), 250)
  }

  const hasUnseen = VERSION !== 'dev' && lastSeen !== VERSION
  const buildTimeLabel = formatBuildTime(BUILD_TIME)

  function openModal() {
    setHoverOpen(false)
    setModalOpen(true)
    localStorage.setItem(lastSeenKey, VERSION)
    setLastSeen(VERSION)
  }

  return (
    <div
      className="relative"
      onMouseEnter={openBubble}
      onMouseLeave={scheduleCloseBubble}
    >
      <button
        onClick={openModal}
        className="relative p-2 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
        title="Verzia systému"
      >
        <Info size={18} />
        {hasUnseen && (
          <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-green-500" />
        )}
      </button>

      {hoverOpen && (
        <div className="absolute right-0 top-full mt-1 w-64 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg p-3 z-50 text-sm">
          <p className="font-semibold text-gray-900 dark:text-white">Verzia {VERSION}</p>
          {buildTimeLabel && (
            <p className="mt-1 text-gray-500 dark:text-gray-400">Aktualizované {buildTimeLabel}</p>
          )}
          {GIT_SHA && (
            <p className="mt-0.5 text-xs text-gray-400 dark:text-gray-500 font-mono">#{GIT_SHA}</p>
          )}
          <button onClick={openModal} className="mt-2 text-brand-600 hover:underline font-medium">
            Čo je nové →
          </button>
        </div>
      )}

      {modalOpen && (
        <Modal title="Čo je nové" onClose={() => setModalOpen(false)} size="lg">
          <div className="p-5 space-y-5 max-h-[60vh] overflow-y-auto">
            {RELEASES.length === 0 && (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Verzia {VERSION}
                {buildTimeLabel ? ` — aktualizované ${buildTimeLabel}` : ''}.
              </p>
            )}
            {RELEASES.map((release) => (
              <div key={release.version}>
                <h3 className="font-semibold text-gray-900 dark:text-white">{release.version}</h3>
                {release.sections.map((section, i) => (
                  <div key={i} className="mt-1.5">
                    {section.title && (
                      <p className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
                        {section.title}
                      </p>
                    )}
                    <ul className="mt-1 space-y-1 list-disc pl-5 text-sm text-gray-700 dark:text-gray-300">
                      {section.items.map((item, j) => (
                        <li key={j}>{item}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </Modal>
      )}
    </div>
  )
}
